import React, { useEffect, useState } from "react";

import Timeline, { TimelineHeaders, SidebarHeader, DateHeader, TodayMarker } from "react-calendar-timeline";
import containerResizeDetector from 'react-calendar-timeline/lib/resize-detector/container';
import "react-calendar-timeline/lib/Timeline.css";
import Fullscreen from "react-full-screen";

import moment from "moment";

import { Menu, MenuItem, Divider, CircularProgress, Popover, Box } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ListIcon from "@mui/icons-material/List";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { Button } from "@cofano/cck";

import usePopover from "components/hooks/usePopover";

import { intl } from "LocaleWrapper";

import "./styling/planboard.scss";

const Periods = {
	HOUR: "hour",
	DAY: "day",
	WEEK: "week",
	MONTH: "month",
};

const keys = {
	groupIdKey: "id",
	groupTitleKey: "title",
	groupRightTitleKey: "rightTitle",
	itemIdKey: "id",
	itemTitleKey: "title",
	itemDivTitleKey: "title",
	itemGroupKey: "group",
	itemTimeStartKey: "start",
	itemTimeEndKey: "end",
};

const getPeriod = (period) => {
	switch (period) {
		case Periods.HOUR:
			return {
				start: moment().startOf("day").toDate(),
				end: moment().startOf("day").add(24, "hour").toDate(),
			};
		case Periods.DAY:
			return {
				start: moment().startOf("day").add(-12, "hour").toDate(),
				end: moment().startOf("day").add(36, "hour").toDate(),
			};
		case Periods.WEEK:
			return {
				start: moment().startOf("week").toDate(),
				end: moment().startOf("week").add(7, "day").toDate(),
			};
		case Periods.MONTH:
			return {
				start: moment().startOf("month").toDate(),
				end: moment().startOf("month").add(1, "month").toDate(),
			};
		default:
			return {
				start: moment().startOf("day").add(-12, "hour").toDate(),
				end: moment().startOf("day").add(36, "hour").toDate(),
			};
	}
};

/**
 * Planboard props
 *
 * @param {String} id - specific id for the planboard
 * @param planboardKey
 * @param {Boolean} loading - is the planboard in loading state
 * @param {Array} groups - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Array} items - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Array} markers - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Array} selected - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Function} onItemSelect - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Function} onItemDeselect - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Function} onItemMove - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Function} onItemResize - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Function} groupRenderer - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Function} itemRenderer - https://github.com/namespace-ee/react-calendar-timeline#api
 * @param {Function} onRefresh - function called to refresh the data on the planboard
 * @param {React.ReactNode} legend - add a legend for some items colors
 * @param {React.ReactNode} optionalTools - optional tools for the planboard
 * @param {Boolean} stackItems - place items belowe eatch other
 * @param {Function} onCanvasClick
 */
const Planboard = ({
	id,
	planboardKey,
	loading,
	scrollToDate,
	groups,
	items,
	markers,
	selected,
	onItemSelect,
	onItemDeselect,
	onItemMove,
	onItemResize,
	groupRenderer,
	itemRenderer,
	onRefresh,
	legend,
	optionalTools,
	stackItems,
	onCanvasClick,
	dragSnap,
}) => {

	const [ fullscreenEnabled, enableFullscreen ] = useState(false);

	const defaultPeriod = localStorage.getItem(`${id}_period`) || Periods.DAY;
	const defaultWindow = getPeriod(defaultPeriod);

	const [ boardItems, setBoardItems ] = useState(items);

	const [ state, setState ] = useState({
		period: defaultPeriod,
		start: defaultWindow.start,
		end: defaultWindow.end,
	});

	const scrollIntoView = (start) => {
		const getEnd = () => {
			switch (state.period) {
				case Periods.HOUR:
					return moment().add(24, "hour").toDate();
				case Periods.DAY:
					return moment().add(48, "hour").toDate();
				case Periods.WEEK:
					return moment().add(7, "day").toDate();
				case Periods.MONTH:
					return moment().add(30, "day").toDate();
				default:
					return moment().add(48, "hour");
			}
		};

		setState(oldState => ({
			...oldState,
			start: moment(start).toDate(),
			end: getEnd(),
		}));
	};

	const onToday = () => {
		scrollIntoView(moment());
	};

	const onChangePeriod = (period) => {

		localStorage.setItem(`${id}_period`, period);

		const { start, end } = getPeriod(period);

		setState(oldState => ({
			...oldState,
			period: period,
			start: start,
			end: end,
		}));
	};

	useEffect(() => {
		setBoardItems(items);
	}, [ items ]);

	useEffect(() => {
		if (scrollToDate) {
			scrollIntoView(moment(scrollToDate));
		}
	}, [ scrollToDate ]);

	if (loading) {
		return (
			<div style={{ padding: 16 }}>
				<CircularProgress size={24} />
			</div>
		);
	} else if (groups?.length === 0 && items?.length === 0) {
		return (
			<div style={{ padding: 16 }}>
				{intl.formatMessage({ id: "NO_DATA_FOUND" })}
			</div>
		);
	} else {
		return (
			<Fullscreen enabled={fullscreenEnabled} onChange={isFullscreen => enableFullscreen(isFullscreen)}>
				<div className={`planboard ${fullscreenEnabled ? "fullscreen" : ""}`}>
					<Tools
						id={id}
						period={state.period}
						onPeriodChange={period => onChangePeriod(period)}
						onToday={() => onToday()}
						onPrevious={() => onAnimate(-1)}
						onNext={() => onAnimate(1)}
						onFullscreen={() => enableFullscreen(!fullscreenEnabled)}
						onRefresh={() => onRefresh()}
						legend={legend}
						optionalTools={optionalTools} />

					{groups?.length > 0 && (
						<Timeline
							key={planboardKey}
							resizeDetector={containerResizeDetector}
							itemHeightRatio={0.75}
							traditionalZoom={true}
							groups={groups}
							onCanvasClick={onCanvasClick}
							items={boardItems}
							keys={keys}
							groupRenderer={groupRenderer}
							itemRenderer={itemRenderer}
							canMove={false}
							canResize={false}
							dragSnap={dragSnap || 24 * 60 * 60 * 1000}
							selected={selected}
							onItemSelect={(itemId, event) => {
								const currentTask = boardItems.find(task => task.id === itemId);
								const start = moment(currentTask.start).unix() * 1000;
								const end = moment(currentTask.end).unix() * 1000;
								const group = groups.find(item => item.id === currentTask.group);

								if (onItemSelect) {
									onItemSelect(currentTask?.entity, start, end, group, event);
								}
							}}
							onItemDeselect={onItemDeselect}
							visibleTimeStart={state.start}
							visibleTimeEnd={state.end}
							stackItems={stackItems}
							onItemMove={(itemId, dragTime, newGroupOrder) => {

								const group = groups[newGroupOrder];
								const currentTask = boardItems.find(task => task.id === itemId);
								const start = dragTime;
								const end = moment(dragTime + (moment(currentTask?.end).unix() * 1000 - moment(currentTask?.start).unix() * 1000)).unix() * 1000;

								setBoardItems([ ...boardItems ].map(item => {
									if (item.id === itemId) {
										return {
											...item,
											start: moment(start),
											end: moment(end),
											group: group.id,
										};
									} else {
										return item;
									}
								}));

								if (onItemMove) {
									onItemMove(currentTask?.entity, start, end, group);
								}
							}}
							onItemResize={(itemId, time, edge) => {

								const currentTask = boardItems.find(task => task.id === itemId);
								const start = edge === "left" ? time : (moment(currentTask?.start).unix() * 1000);
								const end = edge === "left" ? (moment(currentTask?.end).unix() * 1000) : time;

								setBoardItems([ ...boardItems ].map(item => {
									if (item.id === itemId) {
										return {
											...item,
											start: moment(start),
											end: moment(end),
										};
									} else {
										return item;
									}
								}));

								const group = groups.find(item => item.id === currentTask?.group);

								if (onItemResize) {
									onItemResize(currentTask?.entity, start, end, group);
								}
							}}
							onTimeChange={(start, end) => {
								if (!scrollToDate) {
									setState(oldState => ({
										...oldState,
										start: moment(start),
										end: moment(end),
									}));
								}
							}}>
							<TimelineHeaders className="sticky">
								<SidebarHeader>
									{({ getRootProps }) => {
										return <div {...getRootProps()} />;
									}}
								</SidebarHeader>
								<DateHeader unit="primaryHeader" />
								<DateHeader />
							</TimelineHeaders>

							<TodayMarker interval={2000}>
								{({ styles }) => <div style={{ ...styles, backgroundColor: "#EA7676" }} /> }
							</TodayMarker>

							{markers || []}
						</Timeline>
					)}
				</div>
			</Fullscreen>
		);
	}
};

const Tools = ({ id, period, onPeriodChange, onToday, onFullscreen, onPrevious, onNext, onRefresh, legend, optionalTools }) => {
	const { popoverProps, onPopoverOpen } = usePopover({
		trigger: "click",
	});

	return (
		<div className="tools-container">
			<div className="optional-tools">
				{optionalTools}
			</div>

			<div className="tools">
				<ActionButton
					actions={Object.keys(Periods).map((key) => ({
						text: intl.formatMessage({ id: key }),
						onClick: () => onPeriodChange(Periods[key]),
						selected: period === Periods[key],
					}))}
					size="small"
					variant="outlined">
					{intl.formatMessage({ id: period.toUpperCase() })}
				</ActionButton>

				<Button onClick={onToday} size="small" variant="outlined">
					{intl.formatMessage({ id: "TODAY" }).toLowerCase()}
				</Button>

				<Button
					onClick={onPrevious}
					icon={<NavigateBeforeIcon />}
					size="small"
					variant="outlined"
				/>

				<Button
					onClick={onNext}
					icon={<NavigateNextIcon />}
					size="small"
					variant="outlined"
				/>

				<Button
					onClick={onFullscreen}
					icon={<FullscreenIcon />}
					size="small"
					variant="outlined"
				/>

				{legend && (
					<>
						<Button
							onClick={onPopoverOpen}
							icon={<ListIcon />}
							size="small"
							variant="outlined"
						/>

						<Popover {...popoverProps}>
							<Box p={2}>
								{legend}
							</Box>
						</Popover>
					</>
				)}

				<RefreshButton id={id} onRefresh={onRefresh} />

				<Button
					onClick={() => onRefresh()}
					icon={<RefreshIcon />}
					size="small"
					variant="outlined"
				/>
			</div>
		</div>
	);
};

export const RefreshButton = ({ id, onRefresh }) => {

	const getStoredMinutes = () => {
		if (localStorage.getItem(`${id}_refreshInterval`)) {
			return Number(localStorage.getItem(`${id}_refreshInterval`));
		} else {
			return null;
		}
	};

	const [ minutesToRefresh, setMinutesToRefresh ] = useState(getStoredMinutes());

	const refreshLabel = intl.formatMessage({ id: "UPDATES_REFRESH" });
	const offLabel = intl.formatMessage({ id: "OFF" });
	const minuteShortLabel = intl.formatMessage({ id: "MINUTE_SHORT" });

	useEffect(() => {
		localStorage.setItem(`${id}_refreshInterval`, minutesToRefresh);

		const newInterval = minutesToRefresh ? setInterval(() => {
			onRefresh();
		}, ((minutesToRefresh * 60) * 1000)) : null;

		return () => {
			if (newInterval) {
				clearInterval(newInterval);
			}
		};
	}, [ minutesToRefresh ]);

	return (
		<React.Fragment>
			<ActionButton
				actions={[ {
					text: intl.formatMessage({ id: "OFF" }),
					onClick: () => setMinutesToRefresh(null),
					selected: !minutesToRefresh,
					bottomDivider: true,
				}, {
					text: intl.formatMessage({ id: "PLANBOARD_ONE_MINUTE" }),
					onClick: () => setMinutesToRefresh(1),
					selected: minutesToRefresh === 1,
				}, {
					text: intl.formatMessage({ id: "PLANBOARD_THREE_MINUTES" }),
					onClick: () => setMinutesToRefresh(3),
					selected: minutesToRefresh === 3,
				}, {
					text: intl.formatMessage({ id: "PLANBOARD_FIVE_MINUTES" }),
					onClick: () => setMinutesToRefresh(5),
					selected: minutesToRefresh === 5,
				}, {
					text: intl.formatMessage({ id: "PLANBOARD_TEN_MINUTES" }),
					onClick: () => setMinutesToRefresh(10),
					selected: minutesToRefresh === 10,
				} ]}
				size="small"
				variant="outlined">
				{`${refreshLabel} ${minutesToRefresh ? `(${minutesToRefresh} ${minuteShortLabel})` : `(${offLabel})`}`}
			</ActionButton>
		</React.Fragment>
	);
};

const ActionButton = ({ actions, ...rest }) => {

	const [  anchorEl, setAnchorEl ] = useState(null);

	const handleOnClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleOnClose = () => {
		setAnchorEl(null);
	};

	return (
		<React.Fragment>
			<Button {...rest} onClick={handleOnClick} icon={<KeyboardArrowDownIcon fontSize="small" />} />

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleOnClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}>
				{actions.map((action, index) => (
					<div key={index}>
						<MenuItem
							onClick={(event) => {
								handleOnClose();
								action.onClick(event);
							}}
							selected={action.selected}
							dense>
							{action.text}
						</MenuItem>

						{action.bottomDivider && <Divider sx={{ my: 0.5 }} />}
					</div>
				))}
			</Menu>
		</React.Fragment>
	);
};

const onAnimate = (invert) => {

	const rctScroll = document.getElementsByClassName("rct-scroll")[0];
	const width = (invert) * parseFloat(rctScroll.style.width);
	const duration = 1000;
	const startTime = performance.now();

	let lastWidth = 0;

	const animate = () => {
		let normalizedTime = (performance.now() - startTime) / duration;

		if (normalizedTime > 1) {
			normalizedTime = 1;
		}

		const calculatedWidth = Math.floor(width * 0.5 * (1 + Math.cos(Math.PI * (normalizedTime - 1))));
		rctScroll.scrollLeft += calculatedWidth - lastWidth;
		lastWidth = calculatedWidth;

		if (normalizedTime < 1) {
			requestAnimationFrame(animate);
		}
	};

	requestAnimationFrame(animate);
};

export default Planboard;
